/**
 * The `save_outside_result` card's own fields (ORA-1184), read from the
 * proposal's args. The backend spreads them into the args alongside the older
 * ones (provider, formLabel, takenAt, overall, sourceKind, …); a card proposed
 * before them has no `headline`/`effect`, and `readOutsideResultCard` returns
 * null so the generic layout shows it instead.
 */

export const OUTSIDE_RESULT_ACTION = 'save_outside_result';

export type OutsideResultEffect = 'none' | 'partial' | 'full';
export type OutsideResultSelfBand = 'lower' | 'same' | 'higher';
export type OutsideResultComparisonBand = 'lower' | 'average' | 'higher';
export type OutsideResultSourceKind = 'DOCUMENT' | 'PASTED' | 'TYPED';

export interface OutsideResultUsedArea {
  label: string;
  topics: { id: string; name: string }[];
  moreTopicCount: number;
  bandVsSelf: OutsideResultSelfBand | null;
  bandVsComparison: OutsideResultComparisonBand | null;
  valueText: string | null;
  itemCount: number;
}

export interface OutsideResultNotUsedGroup {
  reasonCode: string;
  reasonText: string;
  labels: string[];
}

export interface OutsideResultHistoryPoint {
  takenAt: string;
  value: number;
}

export interface OutsideResultCard {
  /** "NBME CBSSA Form 27": the provider and the form, as one title. */
  title: string;
  takenAt: string | null;
  /** The exam the result is saved under; absent on older cards. */
  examName: string | null;
  overall: { value: string; scaleLabel: string | null } | null;
  sourceLabel: string | null;
  headline: string;
  reason: string | null;
  effect: OutsideResultEffect;
  override: string | null;
  usedAreas: OutsideResultUsedArea[];
  notUsedGroups: OutsideResultNotUsedGroup[];
  previousOverall: OutsideResultHistoryPoint[];
  /** Listed questions the per-question list could not use yet. */
  ignoredItemCount: number;
  /** Set for a repeat of a saved result; the proposal summary then says how. */
  isRepeat: boolean;
  confirmLabel: string | null;
}

const PROVIDER_LABELS: Record<string, string> = {
  nbme: 'NBME',
  uworld: 'UWorld',
  amboss: 'AMBOSS',
  nbome: 'NBOME',
  kaplan: 'Kaplan',
  ati: 'ATI',
  hesi: 'HESI',
  archer: 'Archer',
  school: 'School',
  ora: 'Ora',
  other: 'Outside',
};

/** The backend writes the scale with spaces for underscores ("three digit"). */
const SCALE_LABELS: Record<string, string> = {
  percent: 'Percent',
  epc: 'EPC',
  'three digit': '3-digit score',
  percentile: 'Percentile',
  'probability pass': 'Pass probability',
  band: 'Band',
  other: 'Score',
};

const SOURCE_LABELS: Record<OutsideResultSourceKind, string> = {
  DOCUMENT: 'Uploaded report',
  PASTED: 'Pasted text',
  TYPED: 'Self-reported',
};

const EFFECTS: readonly OutsideResultEffect[] = ['none', 'partial', 'full'];

const SELF_BANDS: readonly OutsideResultSelfBand[] = [
  'lower',
  'same',
  'higher',
];

const COMPARISON_BANDS: readonly OutsideResultComparisonBand[] = [
  'lower',
  'average',
  'higher',
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;

const oneOf = <T extends string>(
  value: unknown,
  allowed: readonly T[]
): T | null =>
  typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null;

const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** "248 three digit" → value "248", scale "3-digit score". */
const readOverall = (value: unknown): OutsideResultCard['overall'] => {
  const overall = text(value);
  if (!overall) return null;

  const match = /^(\S+)\s+(.+)$/.exec(overall);
  if (!match) return { value: overall, scaleLabel: null };

  const [, score, scale] = match;
  const scaleLabel = SCALE_LABELS[scale.toLowerCase()];

  // A scale this client does not know: show the backend's text whole rather
  // than drop half of it.
  return scaleLabel
    ? { value: score, scaleLabel }
    : { value: overall, scaleLabel: null };
};

const readUsedArea = (value: unknown): OutsideResultUsedArea[] => {
  if (!isRecord(value)) return [];
  const label = text(value.label);
  if (!label) return [];

  return [
    {
      label,
      topics: list(value.topics).flatMap((topic) => {
        if (!isRecord(topic)) return [];
        const id = text(topic.id);
        const name = text(topic.name);
        return id && name ? [{ id, name }] : [];
      }),
      moreTopicCount: count(value.moreTopicCount),
      bandVsSelf: oneOf(value.bandVsSelf, SELF_BANDS),
      bandVsComparison: oneOf(value.bandVsComparison, COMPARISON_BANDS),
      valueText: text(value.valueText),
      itemCount: count(value.itemCount),
    },
  ];
};

const readNotUsedGroup = (value: unknown): OutsideResultNotUsedGroup[] => {
  if (!isRecord(value)) return [];
  const reasonText = text(value.reasonText);

  const labels = list(value.labels).flatMap((label) => {
    const read = text(label);
    return read ? [read] : [];
  });

  if (!reasonText || labels.length === 0) return [];

  return [{ reasonCode: text(value.reasonCode) ?? '', reasonText, labels }];
};

const readHistoryPoint = (value: unknown): OutsideResultHistoryPoint[] => {
  if (!isRecord(value)) return [];
  const takenAt = text(value.takenAt);
  const score = value.value;

  return takenAt && typeof score === 'number' && Number.isFinite(score)
    ? [{ takenAt, value: score }]
    : [];
};

/**
 * The dedicated card's data, or null when this proposal is not an outside
 * result or predates the dedicated card's fields (no headline or effect).
 */
export const readOutsideResultCard = (
  name: string,
  args: Record<string, unknown>
): OutsideResultCard | null => {
  if (name !== OUTSIDE_RESULT_ACTION) return null;

  const headline = text(args.headline);
  const effect = oneOf(args.effect, EFFECTS);
  if (!headline || !effect) return null;

  const providerKey = text(args.provider)?.toLowerCase() ?? null;

  const provider = providerKey
    ? (PROVIDER_LABELS[providerKey] ?? providerKey.toUpperCase())
    : null;

  const title =
    [provider, text(args.formLabel)].filter(Boolean).join(' ') ||
    'Outside result';

  const sourceKind = oneOf<OutsideResultSourceKind>(args.sourceKind, [
    'DOCUMENT',
    'PASTED',
    'TYPED',
  ]);

  return {
    title,
    takenAt: text(args.takenAt),
    examName: text(args.examName),
    overall: readOverall(args.overall),
    sourceLabel: sourceKind ? SOURCE_LABELS[sourceKind] : null,
    headline,
    reason: text(args.reason),
    effect,
    override: text(args.override),
    usedAreas: list(args.usedAreas).flatMap(readUsedArea),
    notUsedGroups: list(args.notUsedGroups).flatMap(readNotUsedGroup),
    previousOverall: list(args.previousOverall).flatMap(readHistoryPoint),
    ignoredItemCount: count(args.ignoredItemCount),
    isRepeat: text(args.repeatKind) !== null,
    confirmLabel: text(args.confirmLabel),
  };
};

/**
 * "Feb 3, 2020" for an ISO date or datetime. Read in UTC: the backend stores
 * the day the test was taken, and a local-time reading would move it a day
 * west of Greenwich.
 */
export const formatOutsideResultDate = (value: string): string => {
  const parsed = Date.parse(
    /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00Z` : value
  );

  if (!Number.isFinite(parsed)) return value;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
};

export const COMPARISON_BAND_LABELS: Record<
  OutsideResultComparisonBand,
  string
> = {
  higher: 'Above average',
  average: 'Average',
  lower: 'Below average',
};

/** The total count of report areas the card lists as not used. */
export const notUsedAreaCount = (card: OutsideResultCard): number =>
  card.notUsedGroups.reduce((total, group) => total + group.labels.length, 0);
